/**
 * Top-level site navigation — one definition shared by the desktop bar
 * (MegaNav) and the mobile drawer (MobileDrawer) so the two can never drift.
 *
 * Every entry points at a route that already exists; nothing here creates a
 * route. Dropdown links reuse the ToolLink data in ./tools so labels and
 * blurbs stay consistent with the homepage, gallery, and footer.
 *
 * Every link from the previous nav (Home, Gallery, How to Use and all 15
 * links in the old "Features" mega-panel) is still reachable from one of the
 * dropdowns below.
 */
import {
  TOOLS,
  WORKSPACE_LINKS,
  EDUCATOR_LINKS,
  EDUCATOR_BLOG_LINK,
  MEGA_PANEL_GAMING,
  MEGA_PANEL_TOOLS,
  type ToolLink,
} from './tools';

export interface NavGroupHeading {
  group: string;
}

export interface NavLinkEntry {
  to: string;
  label: string;
  blurb?: string;
  emoji?: string;
}

export type NavDropdownEntry = NavGroupHeading | NavLinkEntry;

export interface TopNavItem {
  id: string;
  label: string;
  /** Direct link — mutually exclusive with `dropdown`. */
  to?: string;
  dropdown?: NavDropdownEntry[];
}

export const isGroupHeading = (e: NavDropdownEntry): e is NavGroupHeading =>
  (e as NavGroupHeading).group !== undefined;

const ALL_TOOLS: ToolLink[] = [
  ...TOOLS,
  ...WORKSPACE_LINKS,
  ...EDUCATOR_LINKS,
  ...MEGA_PANEL_TOOLS,
  ...MEGA_PANEL_GAMING,
];

/** Look up an existing tool entry by route; throws in dev if a route drifts. */
const tool = (to: string): NavLinkEntry => {
  const t = ALL_TOOLS.find(x => x.to === to);
  if (!t) throw new Error(`topNav: no tool entry for ${to}`);
  return { to: t.to, label: t.label, blurb: t.blurb, emoji: t.emoji };
};

const page = (to: string, label: string, blurb?: string, emoji?: string): NavLinkEntry => ({
  to, label, blurb, emoji,
});

export const TOP_NAV: TopNavItem[] = [
  {
    id: 'wheel',
    label: 'Wheel',
    dropdown: [
      page('/', 'Random Name Wheel', 'Pick a random winner from any list of names.', '🎡'),
      tool('/image-picker-wheel/'),
      tool('/weighted-wheel-spinner/'),
      tool('/yes-no-wheel/'),
      tool('/number-wheel/'),
      tool('/random-letter-wheel/'),
      tool('/decision-wheel/'),
      tool('/linked-wheels/'),
    ],
  },
  {
    id: 'templates',
    label: 'Templates',
    dropdown: [
      { group: 'Browse' },
      page('/gallery/', 'Template Gallery', 'Ready-made wheels you can open and spin.', '🖼️'),
      page('/names/', 'Name Lists', 'Curated lists for babies, dogs, characters and usernames.', '✨'),
      { group: 'Popular' },
      tool('/prize-wheel/'),
      tool('/wheel-of-fortune-maker/'),
      tool('/trivia-wheel/'),
      tool('/spin-the-bottle/'),
      { group: 'Gaming & Pop Culture' },
      ...MEGA_PANEL_GAMING.map(g => tool(g.to)),
    ],
  },
  {
    id: 'education',
    label: 'Education',
    dropdown: [
      tool('/classroom-name-picker/'),
      tool('/random-team-generator/'),
      tool('/classes/'),
      tool('/classroom/resources/'),
      page(
        EDUCATOR_BLOG_LINK.to,
        EDUCATOR_BLOG_LINK.label,
        EDUCATOR_BLOG_LINK.blurb,
        EDUCATOR_BLOG_LINK.emoji,
      ),
    ],
  },
  { id: 'giveaways', label: 'Giveaways', to: '/giveaway-wheel/' },
  {
    id: 'resources',
    label: 'Resources',
    dropdown: [
      page('/how-to/', 'How to Use', 'A quick tour of every feature.', '📖'),
      page('/faq/', 'FAQ', 'Answers to common questions.', '❓'),
      page('/blog/', 'Blog', 'Guides, ideas and activities.', '📝'),
      tool('/embed-spinner-wheel/'),
      page('/contact/', 'Contact', 'Questions, feedback, or ideas.', '✉️'),
    ],
  },
  { id: 'about', label: 'About', to: '/about/' },
];

export const PRIMARY_CTA = { label: 'Spin the Wheel', to: '/' } as const;
